/**
 * Interface sounds.
 *
 * Synthesised with the Web Audio API rather than shipped as audio files: three
 * short tones would otherwise mean three network requests, a cache to manage,
 * and a delay on the very first correct answer — the one moment where instant
 * feedback matters most. Synthesis is sample-accurate and costs nothing.
 *
 * The tuning follows the brand voice. A miss is *never* a buzzer: it is a
 * short, soft, low note that reads as "not yet" rather than an alarm. The app
 * has spent a lot of effort refusing to call her wrong, and a harsh error
 * sound would undo that in a way no wording could recover from.
 */

const STORAGE_KEY = 'pj.sound.v1'

/** Kept well below the pronunciation clips, which are the point of the app. */
const MASTER_GAIN = 0.12

let context: AudioContext | null = null

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  try {
    // Created lazily on the first sound, which always follows a tap — browsers
    // refuse to start an AudioContext without a user gesture.
    context ??= new AudioContext()
    // Safari suspends the context when a PWA is backgrounded; resuming is a
    // no-op when it is already running.
    if (context.state === 'suspended') void context.resume()
    return context
  } catch {
    return null
  }
}

export function isSoundEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off'
  } catch {
    return true
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off')
  } catch {
    // A locked-down storage costs the preference, not the app.
  }
}

interface ToneSpec {
  frequency: number
  /** Seconds from now. */
  startAt: number
  duration: number
  type: OscillatorType
  peak: number
}

function playTones(tones: readonly ToneSpec[]): void {
  const ctx = getContext()
  if (!ctx) return

  const now = ctx.currentTime
  for (const tone of tones) {
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.type = tone.type
    oscillator.frequency.value = tone.frequency

    const start = now + tone.startAt
    const end = start + tone.duration

    // A ramped envelope rather than a raw start/stop: switching a gain node
    // instantly produces an audible click at both ends of every note.
    gain.gain.setValueAtTime(0.0001, start)
    gain.gain.exponentialRampToValueAtTime(tone.peak * MASTER_GAIN, start + 0.015)
    gain.gain.exponentialRampToValueAtTime(0.0001, end)

    oscillator.connect(gain).connect(ctx.destination)
    oscillator.start(start)
    oscillator.stop(end + 0.02)
  }
}

/** Two quick rising notes — a major third, which reads as resolved and warm. */
export function playCorrect(): void {
  if (!isSoundEnabled()) return
  playTones([
    { frequency: 660, startAt: 0, duration: 0.09, type: 'sine', peak: 0.9 },
    { frequency: 880, startAt: 0.07, duration: 0.16, type: 'sine', peak: 1 },
  ])
}

/**
 * One soft low note. Deliberately not a buzzer, not dissonant, and quieter
 * than the correct sound — it marks the moment without punishing it.
 */
export function playIncorrect(): void {
  if (!isSoundEnabled()) return
  playTones([
    { frequency: 300, startAt: 0, duration: 0.12, type: 'triangle', peak: 0.55 },
    { frequency: 240, startAt: 0.1, duration: 0.18, type: 'triangle', peak: 0.45 },
  ])
}

/** Partial credit: the rising shape of `correct`, stopping short of the top. */
export function playPartial(): void {
  if (!isSoundEnabled()) return
  playTones([
    { frequency: 520, startAt: 0, duration: 0.09, type: 'sine', peak: 0.7 },
    { frequency: 620, startAt: 0.07, duration: 0.15, type: 'sine', peak: 0.7 },
  ])
}

/** A three-note major arpeggio for finishing a lesson. */
export function playLessonComplete(): void {
  if (!isSoundEnabled()) return
  playTones([
    { frequency: 523, startAt: 0, duration: 0.12, type: 'sine', peak: 0.8 },
    { frequency: 659, startAt: 0.11, duration: 0.12, type: 'sine', peak: 0.85 },
    { frequency: 784, startAt: 0.22, duration: 0.3, type: 'sine', peak: 1 },
  ])
}

/** Routes a graded result to the right tone. */
export function playResult(result: { correct: boolean; score: number }): void {
  if (result.correct) playCorrect()
  else if (result.score > 0) playPartial()
  else playIncorrect()
}
